/* The text, number, choice and date families.
 *
 * Between them these cover most of every ERPNext form. The care in here is
 * mostly about two things people get wrong in an ERP and then blame
 * themselves for:
 *
 *   - a number that is formatted while you are typing in it, so the caret
 *     jumps and you cannot tell what you actually entered;
 *   - a required field that only announces itself after you press Save.
 */
import { useEffect, useRef, useState } from 'react'
import type { Control } from './kit'
import { Field, asCheck, asNumber, asText } from './kit'
import { formatCurrency, formatNumber, selectOptions } from '../format'

const id = (name: string) => `f_${name}`

export const DataControl: Control = (p) => (
  <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
    <input
      id={id(p.field.fieldname)} className="input" type="text"
      value={asText(p.value)} readOnly={p.readOnly} required={p.required}
      autoFocus={p.autoFocus} maxLength={p.field.length || undefined}
      aria-invalid={p.error ? true : undefined}
      onChange={(e) => p.onChange(e.target.value)}
    />
  </Field>
)

export const ReadOnlyControl: Control = (p) => (
  <Field field={p.field} required={false} error={p.error} bare={p.bare}>
    <div className="input" style={{ background: 'var(--sunk)', color: 'var(--ink-2)', display: 'flex', alignItems: 'center' }}>
      {asText(p.value) || <span className="muted">—</span>}
    </div>
  </Field>
)

export const PasswordControl: Control = (p) => (
  <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
    <input id={id(p.field.fieldname)} className="input" type="password" autoComplete="new-password"
           value={asText(p.value)} readOnly={p.readOnly} required={p.required}
           aria-invalid={p.error ? true : undefined}
           onChange={(e) => p.onChange(e.target.value)} />
  </Field>
)

export function makeTextArea(minRows: number): Control {
  return (p) => (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <textarea
        id={id(p.field.fieldname)} className="textarea" rows={minRows}
        value={asText(p.value)} readOnly={p.readOnly} required={p.required}
        autoFocus={p.autoFocus} aria-invalid={p.error ? true : undefined}
        onChange={(e) => p.onChange(e.target.value)}
      />
    </Field>
  )
}

export const CodeControl: Control = (p) => (
  <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
    <textarea
      id={id(p.field.fieldname)} className="textarea mono" rows={8} spellCheck={false}
      value={asText(p.value)} readOnly={p.readOnly}
      onChange={(e) => p.onChange(e.target.value)}
      style={{ tabSize: 2, whiteSpace: 'pre', overflowWrap: 'normal', overflowX: 'auto' }}
    />
  </Field>
)

/** Numbers: formatted when you are not in the box, raw while you type.
 *
 *  Formatting a number under the caret is the single most irritating thing a
 *  form can do — "1,00,000" becomes uneditable — so the formatted version is
 *  only ever shown on blur. */
export function makeNumber(kind: 'Int' | 'Float' | 'Currency' | 'Percent'): Control {
  return (p) => {
    const [typing, setTyping] = useState<string | null>(null)
    const precision = Number(p.field.precision) || (kind === 'Int' ? 0 : 2)
    const raw = asNumber(p.value)

    const shown = typing !== null
      ? typing
      : raw === '' ? ''
      : kind === 'Currency' ? formatCurrency(raw, p.currency ?? 'INR', precision)
      : kind === 'Percent' ? `${formatNumber(raw, precision)}%`
      : formatNumber(raw, kind === 'Int' ? 0 : undefined)

    return (
      <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
        <input
          id={id(p.field.fieldname)} className="input num"
          // `text`, not `number`: a number input silently drops what it
          // cannot parse, so a mistyped amount becomes an empty one and the
          // person never sees what they actually typed.
          type="text" inputMode={kind === 'Int' ? 'numeric' : 'decimal'}
          value={shown} readOnly={p.readOnly} required={p.required} autoFocus={p.autoFocus}
          aria-invalid={p.error ? true : undefined}
          onFocus={() => setTyping(raw === '' ? '' : String(raw))}
          onChange={(e) => setTyping(e.target.value)}
          onBlur={() => {
            if (typing === null) return
            const cleaned = typing.replace(/[^0-9.\-]/g, '')
            const n = cleaned === '' ? null : Number(cleaned)
            setTyping(null)
            if (n === null) { p.onChange(null); return }
            if (Number.isNaN(n)) return
            const bounded = p.field.non_negative && n < 0 ? 0 : n
            p.onChange(kind === 'Int' ? Math.trunc(bounded) : Number(bounded.toFixed(precision)))
          }}
        />
      </Field>
    )
  }
}

export const CheckControl: Control = (p) => (
  <Field field={{ ...p.field, label: '' }} required={false} error={p.error} bare>
    <label className="check">
      <input
        type="checkbox" checked={asCheck(p.value) === 1} disabled={p.readOnly}
        onChange={(e) => p.onChange(e.target.checked ? 1 : 0)}
      />
      <span>{p.field.label || p.field.fieldname}</span>
    </label>
    {p.field.description ? <div className="hint">{p.field.description}</div> : null}
    {p.error ? <div className="err" role="alert">{p.error}</div> : null}
  </Field>
)

export const SelectControl: Control = (p) => {
  const options = selectOptions(p.field.options)
  const sorted = p.field.sort_options ? [...options].sort((a, b) => a.label.localeCompare(b.label)) : options
  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <select
        id={id(p.field.fieldname)} className="select" value={asText(p.value)}
        disabled={p.readOnly} required={p.required} autoFocus={p.autoFocus}
        aria-invalid={p.error ? true : undefined}
        onChange={(e) => p.onChange(e.target.value)}
      >
        {sorted.some((o) => o.value === '') ? null : <option value="">—</option>}
        {sorted.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  )
}

/** Free text with suggestions, unlike Select which is a closed list. */
export const AutocompleteControl: Control = (p) => {
  const listId = `dl_${p.field.fieldname}`
  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <input id={id(p.field.fieldname)} className="input" list={listId} value={asText(p.value)}
             readOnly={p.readOnly} required={p.required}
             onChange={(e) => p.onChange(e.target.value)} />
      <datalist id={listId}>
        {selectOptions(p.field.options).map((o) => <option key={o.value} value={o.value} />)}
      </datalist>
    </Field>
  )
}

export const ColorControl: Control = (p) => (
  <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
    <div className="row">
      <input type="color" value={asText(p.value) || '#000000'} disabled={p.readOnly}
             onChange={(e) => p.onChange(e.target.value)}
             style={{ width: 44, height: 'var(--control-h)', padding: 2, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }} />
      <input className="input mono grow" value={asText(p.value)} readOnly={p.readOnly}
             placeholder="#000000" onChange={(e) => p.onChange(e.target.value)} />
    </div>
  </Field>
)

/** Dates go through the native picker, which every phone already knows how to
 *  show, and which is the only one that is reliably accessible. Frappe's wire
 *  format is already `YYYY-MM-DD`, so nothing is converted. */
export function makeDate(kind: 'Date' | 'Datetime' | 'Time'): Control {
  return (p) => {
    const type = kind === 'Date' ? 'date' : kind === 'Time' ? 'time' : 'datetime-local'
    const text = asText(p.value)
    const shown = kind === 'Datetime' ? text.replace(' ', 'T').slice(0, 16)
      : kind === 'Time' ? text.slice(0, 8) : text.slice(0, 10)
    return (
      <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
        <input
          id={id(p.field.fieldname)} className="input" type={type} value={shown}
          readOnly={p.readOnly} required={p.required} autoFocus={p.autoFocus}
          step={kind === 'Time' && !p.field.hide_seconds ? 1 : undefined}
          aria-invalid={p.error ? true : undefined}
          onChange={(e) => {
            const v = e.target.value
            if (!v) { p.onChange(null); return }
            p.onChange(kind === 'Datetime' ? `${v.replace('T', ' ')}:00` : v)
          }}
        />
      </Field>
    )
  }
}

export const DurationControl: Control = (p) => {
  const total = Math.max(0, Math.trunc(Number(p.value) || 0))
  const set = (part: 'h' | 'm' | 's', n: number) => {
    const h = part === 'h' ? n : Math.floor(total / 3600)
    const m = part === 'm' ? n : Math.floor((total % 3600) / 60)
    const s = part === 's' ? n : total % 60
    p.onChange(h * 3600 + m * 60 + s)
  }
  const box = (label: string, value: number, part: 'h' | 'm' | 's') => (
    <div className="row" style={{ gap: 4 }}>
      <input className="input num" style={{ width: 66 }} inputMode="numeric" value={value}
             readOnly={p.readOnly} onChange={(e) => set(part, Math.max(0, Number(e.target.value) || 0))} />
      <span className="small muted">{label}</span>
    </div>
  )
  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <div className="row">
        {box('h', Math.floor(total / 3600), 'h')}
        {box('m', Math.floor((total % 3600) / 60), 'm')}
        {p.field.hide_seconds ? null : box('s', total % 60, 's')}
      </div>
    </Field>
  )
}

/** A Button on a form calls a server method. Every one of these is also an
 *  entry in the AI action registry (A01), which is why it carries its name. */
export const ButtonControl: Control = (p) => (
  <button className="btn" type="button" disabled={p.readOnly}
          data-action={p.field.fieldname}
          onClick={() => p.onChange({ __action: p.field.fieldname })}>
    {p.field.label || p.field.fieldname}
  </button>
)

/** Rich text. A full editor is ERP-09's job; until then this keeps the HTML
 *  intact rather than silently flattening somebody's formatted terms and
 *  conditions, and says plainly that it is showing markup. */
export const RichTextControl: Control = (p) => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== asText(p.value)) {
      ref.current.innerHTML = asText(p.value)
    }
  }, [p.value])
  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <div
        ref={ref} className="textarea" contentEditable={!p.readOnly} suppressContentEditableWarning
        role="textbox" aria-multiline="true" aria-label={p.field.label || p.field.fieldname}
        style={{ minHeight: 110, overflowY: 'auto' }}
        onBlur={(e) => p.onChange(e.currentTarget.innerHTML)}
      />
    </Field>
  )
}
